import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Clock, PauseCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import AttendanceStatusBadge from './AttendanceStatusBadge';
import {
  formatUKDateTime,
  getElapsedLabel,
  minutesToHoursMinutes,
} from '../../utils/attendanceDateTime';

export default function CurrentShiftCard({
  assignedVenues,
  selectedVenueId,
  setSelectedVenueId,
  activeShift,
  actionLoading,
  onClockIn,
  onStartBreak,
  onEndBreak,
  onClockOut,
  venueLocked,
}) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (!activeShift?.clock_in_at) return undefined;

    const timer = setInterval(() => {
      setNow(new Date());
    }, 30000);

    return () => clearInterval(timer);
  }, [activeShift?.clock_in_at]);

  const selectedVenue = useMemo(
    () => assignedVenues.find((venue) => venue.id === selectedVenueId),
    [assignedVenues, selectedVenueId]
  );

  const isClockedIn = Boolean(activeShift);
  const onBreak = Boolean(activeShift?.has_active_break);

  return (
    <section className="card-rtb border-[#FF6600]/30" data-testid="current-shift-card">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">Current Shift</p>
          <div className="mt-2 flex items-center gap-3">
            <h2 className="font-heading text-3xl uppercase text-white">
              {isClockedIn ? 'Clocked In' : 'Not Clocked In'}
            </h2>
            <AttendanceStatusBadge status={onBreak ? 'on_break' : isClockedIn ? 'clocked_in' : 'not_clocked_in'} />
          </div>
        </div>
        {isClockedIn && (
          <div className="rounded-md border border-[#3F3F46] bg-[#121212] px-4 py-3 min-w-[220px]">
            <p className="text-[#A1A1AA] text-xs uppercase">Live Elapsed</p>
            <p className="text-2xl text-white font-mono mt-1">{getElapsedLabel(activeShift.clock_in_at, now)}</p>
          </div>
        )}
      </div>

      {!isClockedIn && assignedVenues.length > 1 && (
        <div className="mt-5">
          <label className="text-[#A1A1AA] text-sm mb-2 block">Choose Venue</label>
          <Select value={selectedVenueId} onValueChange={setSelectedVenueId} disabled={actionLoading || venueLocked}>
            <SelectTrigger className="input-rtb">
              <SelectValue placeholder="Select your venue" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E1E1E] border-[#3F3F46] text-white">
              {assignedVenues.map((venue) => (
                <SelectItem key={venue.id} value={venue.id}>
                  {venue.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isClockedIn && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-md border border-[#3F3F46] bg-[#121212] px-3 py-2">
            <p className="text-[#A1A1AA] text-xs uppercase">Venue</p>
            <p className="text-white text-sm mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#FF6600]" />
              {activeShift.venue_name || selectedVenue?.name || 'Assigned Venue'}
            </p>
          </div>
          <div className="rounded-md border border-[#3F3F46] bg-[#121212] px-3 py-2">
            <p className="text-[#A1A1AA] text-xs uppercase">Clock In</p>
            <p className="text-white text-sm mt-1 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#FF6600]" />
              {formatUKDateTime(activeShift.clock_in_at)}
            </p>
          </div>
          <div className="rounded-md border border-[#3F3F46] bg-[#121212] px-3 py-2">
            <p className="text-[#A1A1AA] text-xs uppercase">Break Total</p>
            <p className="text-white text-sm mt-1 flex items-center gap-2">
              <PauseCircle className="w-4 h-4 text-[#FF6600]" />
              {minutesToHoursMinutes(activeShift.break_minutes || 0)}
            </p>
          </div>
          <div className="rounded-md border border-[#3F3F46] bg-[#121212] px-3 py-2">
            <p className="text-[#A1A1AA] text-xs uppercase">Status</p>
            <p className="text-white text-sm mt-1">{onBreak ? 'On break' : 'Working'}</p>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        {!isClockedIn && (
          <Button
            disabled={actionLoading || !selectedVenueId}
            onClick={onClockIn}
            className="btn-rtb-primary"
            data-testid="clock-in-btn"
          >
            {actionLoading ? 'Clocking In...' : 'Clock In'}
          </Button>
        )}

        {isClockedIn && !onBreak && (
          <>
            <Button
              disabled={actionLoading}
              onClick={onStartBreak}
              className="bg-[#FFB300] hover:bg-[#FFB300]/90 text-black font-semibold"
              data-testid="start-break-btn"
            >
              {actionLoading ? 'Starting Break...' : 'Start Break'}
            </Button>
            <Button
              disabled={actionLoading}
              onClick={onClockOut}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              data-testid="clock-out-btn"
            >
              {actionLoading ? 'Clocking Out...' : 'Clock Out'}
            </Button>
          </>
        )}

        {isClockedIn && onBreak && (
          <>
            <Button
              disabled={actionLoading}
              onClick={onEndBreak}
              className="bg-[#00E676] hover:bg-[#00E676]/90 text-black font-semibold"
              data-testid="end-break-btn"
            >
              {actionLoading ? 'Ending Break...' : 'End Break'}
            </Button>
            <Button disabled className="opacity-60" data-testid="clock-out-disabled-btn">
              Clock Out (End Break First)
            </Button>
          </>
        )}
      </div>
    </section>
  );
}
